import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const Unsubscribe = () => {
    const { t } = useTranslation();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Get customer ID from localStorage or context
            const custId = localStorage.getItem('userID');
            console.log('Fetching subscriptions for custId:', custId);
            
            const response = await fetch(
                `https://xianhefairy.com/LotusFairy_API/Payment/GetStripeSubscriptionByCustomer?custId=${custId}`
            );
            
            if (!response.ok) {
                console.error('API request failed with status:', response.status);
                throw new Error('Failed to fetch subscriptions');
            }
            
            const result = await response.json();
            console.log('API Response:', result);
            
            if (result.success) {
                console.log('Subscriptions data:', result.data);
                setSubscriptions(result.data || []);
            } else {
                // API responded but indicated no data or unsuccessful result.
                // Show an empty subscriptions list instead of surfacing an error to the user.
                console.warn('API returned unsuccessful response, showing empty list:', result);
                setSubscriptions([]);
            }
        } catch (err) {
            console.error('Error fetching subscriptions:', err);
            console.error('Error message:', err.message);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getBarColor = (index) => {
        const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336'];
        return colors[index % colors.length];
    };

    const handleUnsubscribe = async (subscriptionId, subDesc, uid) => {
        if (!window.confirm(t('unsubscribe.confirmCancel', { subscription: subDesc }))) {
            return;
        }

        try {
            console.log('Attempting to unsubscribe from:', subscriptionId);
            
            const response = await fetch(
                'https://xianhefairy.com/LotusFairy_API/Payment/UnsubscribeDelete',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        SubscriptionId: subscriptionId
                    })
                }
            );

            if (!response.ok) {
                console.error('Unsubscribe request failed with status:', response.status);
                throw new Error('Failed to cancel subscription');
            }

            const result = await response.json();
            console.log('Unsubscribe API Response:', result);

            if (result.success) {
                console.log('UnsubscribeDelete succeeded:', result);

                // Call DeleteStripeSubscription endpoint with UID in body
                try {
                    const deleteResponse = await fetch(
                        'https://xianhefairy.com/LotusFairy_API/Payment/DeleteStripeSubscription',
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ UID: uid })
                        }
                    );

                    if (!deleteResponse.ok) {
                        console.error('DeleteStripeSubscription failed with status:', deleteResponse.status);
                        // don't block the user; log and continue
                    } else {
                        const delResult = await deleteResponse.json().catch(() => null);
                        console.log('DeleteStripeSubscription response:', delResult);
                    }
                } catch (delErr) {
                    console.error('Error calling DeleteStripeSubscription:', delErr);
                }

                alert(t('unsubscribe.cancelSuccess'));
                // Refresh the subscriptions list
                fetchSubscriptions();
            } else {
                console.error('Unsubscribe returned unsuccessful response:', result);
                throw new Error(result.message || t('unsubscribe.cancelFailed'));
            }
        } catch (err) {
            console.error('Error cancelling subscription:', err);
            console.error('Error message:', err.message);
            alert(`${t('unsubscribe.error')}: ${err.message}`);
        }
    };

    // Inline styles
    const styles = {
        container: {
            maxWidth: '900px',
            margin: '0 auto',
            padding: '2rem',
            fontFamily: 'Arial, sans-serif',
            overflowY: 'auto',
            maxHeight: '100vh'
        },
        h1: {
            color: '#333',
            marginBottom: '1rem',
            fontSize: '2rem'
        },
        subscriptionCount: {
            color: '#666',
            fontSize: '1.1rem',
            marginBottom: '2rem'
        },
        loading: {
            textAlign: 'center',
            padding: '3rem',
            fontSize: '1.2rem',
            color: '#666'
        },
        error: {
            textAlign: 'center',
            padding: '3rem',
            fontSize: '1.2rem',
            color: '#f44336'
        },
        button: {
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            padding: '0.8rem 1.5rem',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '1rem',
            marginTop: '1rem',
            transition: 'background-color 0.3s'
        },
        subscriptionsList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            marginBottom: '2rem'
        },
        noSubscriptions: {
            textAlign: 'center',
            padding: '3rem',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            color: '#666',
            fontSize: '1.1rem'
        },
        subscriptionBar: {
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: '10px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s'
        },
        subscriptionInfo: {
            marginBottom: '1rem'
        },
        h3: {
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.5rem'
        },
        subscriptionDetails: {
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.5rem',
            fontSize: '0.9rem',
            flexWrap: 'wrap'
        },
        detailLabel: {
            color: '#666',
            fontWeight: '600'
        },
        detailValue: {
            color: '#333',
            fontFamily: 'monospace',
            backgroundColor: '#f5f5f5',
            padding: '0.2rem 0.5rem',
            borderRadius: '3px'
        },
        barContainer: {
            margin: '1.5rem 0',
            backgroundColor: '#f0f0f0',
            borderRadius: '20px',
            height: '40px',
            overflow: 'hidden',
            position: 'relative'
        },
        barFill: {
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'width 0.5s ease',
            borderRadius: '20px',
            position: 'relative'
        },
        barLabel: {
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '1px'
        },
        subscriptionActions: {
            display: 'flex',
            gap: '1rem',
            marginTop: '1rem',
            flexWrap: 'wrap'
        },
        manageBtn: {
            padding: '0.7rem 1.2rem',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: '600',
            transition: 'all 0.3s',
            backgroundColor: '#4CAF50',
            color: 'white'
        },
        cancelBtn: {
            padding: '0.7rem 1.2rem',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: '600',
            transition: 'all 0.3s',
            backgroundColor: '#f44336',
            color: 'white'
        },
        refreshBtn: {
            display: 'block',
            margin: '0 auto',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            padding: '0.8rem 1.5rem',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'background-color 0.3s'
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>{t('unsubscribe.loading')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>{t('unsubscribe.error')}: {error}</div>
                <button onClick={fetchSubscriptions} style={styles.button}>
                    {t('unsubscribe.retry')}
                </button>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <h1 style={styles.h1}>{t('unsubscribe.title')}</h1>
            <p style={styles.subscriptionCount}>
                {t('unsubscribe.totalSubscriptions')}: {subscriptions.length}
            </p>

            <div style={styles.subscriptionsList}>
                {subscriptions.length === 0 ? (
                    <div style={styles.noSubscriptions}>
                        {t('unsubscribe.noSubscriptions')}
                    </div>
                ) : (
                    subscriptions.map((sub, index) => (
                        <div key={sub.UID} style={styles.subscriptionBar}>
                            <div style={styles.subscriptionInfo}>
                                <h3 style={styles.h3}>{sub.SUB_DESC || t('unsubscribe.defaultName')}</h3>
                                <div style={styles.subscriptionDetails}>
                                    <span style={styles.detailLabel}>{t('unsubscribe.subscriptionId')}:</span>
                                    <span style={styles.detailValue}>{sub.SUB_ID}</span>
                                </div>
                                <div style={styles.subscriptionDetails}>
                                    <span style={styles.detailLabel}>{t('unsubscribe.stripeCustomer')}:</span>
                                    <span style={styles.detailValue}>{sub.STRIPE_ID}</span>
                                </div>
                            </div>
                            <div style={styles.subscriptionActions}>
                                {/* <button style={styles.manageBtn}>Manage</button> */}
                                <button 
                                    style={styles.cancelBtn}
                                    onClick={() => handleUnsubscribe(sub.SUB_ID, sub.SUB_DESC, sub.UID)}
                                >
                                    {t('unsubscribe.cancelButton')}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <button onClick={fetchSubscriptions} style={styles.refreshBtn}>
                {t('unsubscribe.refreshButton')}
            </button>
        </div>
    );
};

export default Unsubscribe;
